import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import { css, StyleSheet } from 'aphrodite';
import { inject } from 'mobx-react';
import React from 'react';
import { FormFieldType } from '../../../../../../../../extViewmodels';
import { Button } from '../../../../../../../components-v2/Button';
import { THEME } from '../../../../../../../components-v2/Theme/theme';
import { asModalComponent } from '../../../../../../../components/Modal';
import { IModalContext, ModalChildComponentContextKey } from '../../../../../../../../models';
import { SurveyQuestion } from '../../types';
import { ReorderableList } from '../ReorderableList/ReorderableList';

// ---------------------------------------------------------------------------
// Result type
// ---------------------------------------------------------------------------

export interface SectionQuestionsReorderResult {
	reorderedQuestionIds?: string[];
	canceled?: boolean;
}

// ---------------------------------------------------------------------------
// Local helpers
// ---------------------------------------------------------------------------

const getFieldTypeLabel = (fieldType: FormFieldType): string => {
	switch (fieldType) {
		case FormFieldType.String:
			return 'Text';
		case FormFieldType.Option:
			return 'Single Choice';
		case FormFieldType.MultipleOptions:
			return 'Multiple Choice';
		case FormFieldType.Boolean:
			return 'Yes/No';
		default:
			return 'Text';
	}
};

// ---------------------------------------------------------------------------
// Local styles
// ---------------------------------------------------------------------------

const {
	colors,
	typography: { fontSizes },
	spacing,
} = THEME;

const styleSheet = StyleSheet.create({
	emptyState: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		padding: spacing.xl,
		gap: spacing.md,
	},
	emptyTitle: {
		color: colors.text.main,
		fontSize: fontSizes.lg,
		fontWeight: 600,
		textAlign: 'center',
	},
	rowTitle: {
		color: colors.text.main,
		fontSize: fontSizes.sm,
		fontWeight: 500,
	},
	rowType: {
		color: colors.text.light,
		fontSize: fontSizes.sm,
	},
	modal: {},
});

// ---------------------------------------------------------------------------
// ContentProps — the testable presentational unit
// ---------------------------------------------------------------------------

interface ContentProps {
	questions: SurveyQuestion[];
	sectionTitle?: string;
	onClose: (result?: SectionQuestionsReorderResult, cancel?: boolean) => void;
}

/**
 * Presentational content for the Section Questions Reorder modal.
 * Contains no MobX — fully testable in isolation.
 */
export const SectionQuestionsReorderModalContent: React.FC<ContentProps> = ({ questions, sectionTitle, onClose }) => {
	/** Renders a single row's content for ReorderableList */
	const renderRow = (question: SurveyQuestion) => {
		const title = question.label?.trim() || 'Untitled Question';
		const typeLabel = getFieldTypeLabel(question.fieldType);
		return (
			<>
				<div className={css(styleSheet.rowTitle)}>{title}</div>
				<div className={css(styleSheet.rowType)}>{typeLabel}</div>
			</>
		);
	};

	if (questions.length === 0) {
		return (
			<div className={css(styleSheet.emptyState)}>
				<div className={css(styleSheet.emptyTitle)}>No questions to reorder</div>
				<Button variant='secondary' type='button' onClick={() => onClose(undefined, true)}>
					Cancel
				</Button>
			</div>
		);
	}

	const subtitle =
		sectionTitle !== undefined
			? `Reordering questions in "${sectionTitle.trim() || 'Untitled section'}"`
			: 'Drag and drop questions to reorder them.';

	return (
		<ReorderableList
			title='Reorder Questions'
			subtitle={subtitle}
			items={questions}
			droppableId='reorder-section-questions'
			renderContent={renderRow}
			onCommit={ids => onClose({ reorderedQuestionIds: ids }, false)}
			onCancel={() => onClose(undefined, true)}
		/>
	);
};

// ---------------------------------------------------------------------------
// Modal wrapper — mirrors ReorderSectionsModal structure
// ---------------------------------------------------------------------------

interface IProps extends IEventLoggingComponentProps, IModalContext<SectionQuestionsReorderResult> {
	questions: SurveyQuestion[];
	sectionTitle?: string;
}

const _SectionQuestionsReorderModal: React.FC<IProps> = ({ questions, sectionTitle, parentModal }) => {
	return (
		<SectionQuestionsReorderModalContent
			questions={questions}
			sectionTitle={sectionTitle}
			onClose={(result, cancel) => {
				if (parentModal) {
					parentModal.onRequestClose(result, cancel);
				}
			}}
		/>
	);
};

const SectionQuestionsReorderModalWithContext = inject(ModalChildComponentContextKey)(_SectionQuestionsReorderModal);
const SectionQuestionsReorderModal = withEventLogging(
	SectionQuestionsReorderModalWithContext,
	'SectionQuestionsReorderModal'
);

export const SectionQuestionsReorderModalComponent = asModalComponent(SectionQuestionsReorderModal, {
	className: css(styleSheet.modal),
	shouldCloseOnOverlayClick: false,
	useDefaultHeader: true,
});
